/**
 * The consent-free signup-channel header (api#753) — React-free by mandate (epic #26).
 *
 * ── Why a header on the requests, and not a beacon ───────────────────────────────────────────────
 * An account becomes established inside the api — in its Firebase auth filter for a provider sign-in, and
 * inside `POST /auth/email-code/confirm` for the email-code flow. Neither has a body this feature could
 * extend, and neither is a request the browser can identify in advance. A header rides whichever one it
 * turns out to be, which lets the api count from inside the establishment seam's existing exactly-once
 * fence, where a replayed or rolled-back signup counts nothing. A separate anonymous beacon could not: it
 * has no request identity to deduplicate on, so every retry would inflate exactly the number this exists to
 * make honest.
 *
 * ── What ACTUALLY carries it, which is broader than "the signup request" ─────────────────────────
 * Every api request that carries a bearer token — which, because `bootstrapSession` calls
 * `ensureAnonymous()` at hydration, means every api request of every visitor, signed in or not. That is
 * load-bearing rather than incidental: `/auth/email-code/confirm` is `permitAll` and carries only the
 * ANONYMOUS bearer, so narrowing the header to real sessions would strip it from the request that
 * establishes the account on the dominant signup path. See `AxiosUtils.attachMeasurementHeaders`.
 *
 * Two consequences a reader should not have to discover:
 *
 *  - The value is per PAGE LOAD, not per signup. After an in-app full navigation the header carries THAT
 *    page load's referrer class (typically `direct`), not the acquisition. The api reads it at TWO seams:
 *    at account establishment, where it moves one aggregate counter and an established account's later
 *    requests are ignored, and on every request that carries it, where it adds one to a daily per-channel
 *    request total (api#1989) and, for the one request per page load that {@link VISIT_START_HEADER}
 *    marks, one to that day's visit total (ui#2805). So the header is not a running claim about where the
 *    visitor came from, and the per-request total is a load signal rather than a traffic mix. The ONE
 *    page load that does not classify its own referrer at all is the return leg of a provider sign-in
 *    redirect, where that referrer is the OAuth handler's rather than the visitor's — see
 *    {@link settleSignInRedirectVerdict}. It reports
 *    whatever its landing URL still proves and otherwise nothing, which is what keeps the counter honest
 *    on mobile, where the redirect is mandatory (ui#830). Until that verdict arrives the referrer is
 *    withheld rather than guessed at — see {@link holdSignupChannelReferrer}.
 *  - The name says `X-Signup-Channel` even though it is sent on every request and read at two seams. Kept
 *    deliberately, as the api keeps it: it names the seam it was introduced for, and a content-accurate
 *    name (`X-Acquisition-Channel`) would read as an invitation to add a third consumer.
 *
 * ── Why it needs no consent of its own, and the honest privacy surface ───────────────────────────
 * The channel value is one member of the closed vocabulary `ATTRIBUTION_CHANNELS` publishes (that
 * constant is the count, so this paragraph states no number of its own — it spelled "seven-word" here
 * once, and the number was stale twice, at ui#2253 and again at ui#2963, before it was removed),
 * derived from exactly two kinds of input:
 *
 *   - the referrer's host CLASS — which family of site the visitor came from, never the referring URL;
 *   - YES/NO ANSWERS about the landing URL — whether an ad-click parameter was present, whether the
 *     landing route matched the download-page pattern (api#1003, owner decision 2026-08-31, which
 *     amended the earlier flat "never from the landing path" rule this paragraph used to state) or the
 *     file-request-page pattern (ui#2253), whether a referral parameter was present (ui#1974), and
 *     whether the campaign label is one of the six we authored for a hand-posted social link (ui#2252).
 *     All but the last are presence checks. The campaign label reads a query VALUE, and is safe because
 *     it is compared against a literal this bundle already holds and answers only yes or no, so what
 *     reaches the wire is still one vocabulary word.
 *
 * The distinction that carries the lawfulness is VALUE versus YES/NO. Every one of those answers is
 * computed HERE, in the browser, against patterns and literals this bundle already holds (the route
 * patterns built from `RECIPIENT_ROUTE_SEGMENTS`, `ORGANIC_SOCIAL_CAMPAIGNS`), and only the resulting
 * vocabulary word is transmitted. So the wire still carries no identifier, no click-id value, no landing
 * path, no query string, no user agent — nothing the api may correlate back to a person. A `recipient-page`
 * header says "this visitor landed somewhere in the download route family" and `request-page` says the
 * same of the file-request family; neither says which transfer or request, or whose. `referral-invite`
 * says a referral parameter was in the URL and never what its value was. What the api keeps is aggregates
 * only: one counter at the establishment seam, and one row per day and channel word in
 * `acquisition_channel_requests` holding a request total and a visit total. Nothing is stored beside the
 * word — no account, no address, no user agent, no referrer, no path — and nothing is correlated with the
 * request it arrived on. That makes it an aggregate statistic rather than personal data, and it stops
 * being one the moment a VALUE about the CALLER — a path, a query, an identifier — is put on this header.
 *
 * ── The visit marker, and why it does not break that either (ui#2805) ─────────────────────────────
 * {@link VISIT_START_HEADER} rides the page load's FIRST request that carries a channel and no other, so
 * the api can count page loads beside requests. It is one fixed value answering one yes/no question the
 * browser computes from nothing but "has this page load already sent it" — no identifier, no counter, no
 * sequence number, no timestamp — so it adds nothing about the caller to the row it moves.
 *
 * ── Why the campaign labels do not travel (kitecorp/transferify-api-service#2598) ────────────────────
 * The `utm_campaign` and `utm_content` labels a landing link carries are not sent: the api counts
 * sign-ups by channel only, and per-campaign counts come from the consented attribution record.
 *
 * State the surface accurately, because it is broader than "one token at signup": a per-page-load channel
 * bucket travels on every api request, beside the stable pseudonymous UID of the anonymous Firebase
 * session. The api never writes it down beside that UID or anything else about the request: it keeps only
 * the aggregate counter and the daily per-channel totals above, which is what the lawfulness rests on — not
 * on the request being uncorrelatable. The moment a dimension is added that could re-identify, or the value
 * is written down beside that UID, the analysis stops holding. Do not do either.
 */

import type {GameArm} from "games/gameArm";
import {
	holdReferrerClassification,
	landedOnDownloadRoute,
	rememberLanding,
	settleRedirectVerdict,
	signupChannel,
	type AttributionChannel,
} from "./adAttribution";

/**
 * The request header the api reads at its establishment seam, kept byte-identical to the name the api
 * publishes in its OpenAPI contract (`components.parameters.SignupChannelHeader.name`, ui#1371). The
 * contract test in this module's spec reads that name out of `api/openapi.json` and fails when the two
 * drift — exactly like `LANDING_PATH_PATTERN_SOURCE` and the channel enum, which were pinned from the
 * start while this one, the value whose drift silently zeroes the whole feature, was not.
 *
 * Drift here costs no error anyone can see: HTTP does not reject an unrecognised request header, so the
 * api's seam finds nothing and counts every signup as `untracked` — a dashboard that reads exactly like a
 * product nobody can classify, discovered whenever somebody next looks at the panel.
 */
export const SIGNUP_CHANNEL_HEADER = "X-Signup-Channel";

/**
 * The visit marker (ui#2805) and its one value, pinned against the api's published contract
 * (`components.parameters.VisitStartHeader`) like the name above: a renamed marker is not an HTTP error,
 * so the api's visit count would silently stay at zero.
 */
export const VISIT_START_HEADER = "X-Visit-Start";
export const VISIT_START_VALUE = "1";

/** This page load's browser context, passed in so the derivation stays testable and SSG-safe. */
export interface LandingContext {
	pathname: string;
	referrer: string;
	search: string;
	host: string;
}

/**
 * The header value for this page load, or `undefined` when there is no honest bucket to send.
 *
 * Records the landing as a side effect, because this can run before the app-root effect that normally does
 * it — {@link rememberLanding} is first-call-wins, so whichever runs first captures the true landing and
 * the other is a no-op. Sending nothing is always allowed: the api treats an absent header exactly as it
 * treats one it cannot parse, and counts the signup as untracked.
 */
export function signupChannelHeaderValue(context: LandingContext): AttributionChannel | undefined {
	rememberLanding(context);
	return signupChannel();
}

/**
 * The download page's word for a group of the transfer-games A/B test (kitecorp/transferify-ui#2889,
 * api#2165), so signups won on the page that offers a game while a download runs can be compared with the
 * page that does not. Only for a landing on the download route: `recipient-page` also answers the referral
 * chip followed from our own host, which is a home-page load with no group of its own, and stays as it is.
 * Without a known group the plain word goes, rather than a guessed one.
 *
 * Still one word about the page load rather than the person: the group is derived from the analytics id a
 * visitor consented to, or is a coin tossed for the page load and stored nowhere (`games/gameArm`).
 */
export function armedChannel(
	channel: AttributionChannel | undefined, downloadLanding: boolean, arm: (() => GameArm) | null,
): AttributionChannel | undefined {
	if (channel !== "recipient-page" || !downloadLanding || arm === null) {
		return channel;
	}
	return arm() === "game" ? "recipient-page-game" : "recipient-page-control";
}

/**
 * Where this page load's group comes from, registered by the app shell (`App.tsx`) before any api request
 * goes out. Injected rather than imported, because the group's own module reaches the analytics and consent
 * layers, and this one sits under every request the app makes.
 */
let recipientPageArm: (() => GameArm) | null = null;

export function registerRecipientPageArm(arm: () => GameArm): void {
	recipientPageArm = arm;
	derived = null;
}

/**
 * The memo behind {@link browserSignupChannel}, BOXED rather than held as a bare channel: `undefined` is a
 * legitimate answer here — the referrer is our own host, a reload mid-session — so a bare
 * `channel === undefined` test would re-derive on every request for exactly the visitors who make the most.
 */
let derived: {channel: AttributionChannel | undefined} | null = null;

/**
 * The header value for this page load, computed on first use and then remembered (ui#1372).
 *
 * The interceptor that attaches the header runs on EVERY outbound api request (see the module note above),
 * and the answer it asks for cannot change within a page load: {@link rememberLanding} is first-call-wins
 * module memory and {@link signupChannel} is pure over it. Without the memo, every request paid for a fresh
 * context object, a `new URL(referrer)` and up to two host-pattern tests to reach the same constant.
 *
 * A `null` landing context is deliberately NOT memoized: it means "no page here" (the SSG pre-render, a Node
 * test) rather than "no channel", so caching it would let a call made before there is a document decide the
 * answer for every real request afterwards.
 */
export function browserSignupChannel(): AttributionChannel | undefined {
	if (derived === null) {
		const context = browserLandingContext();
		if (context === null) {
			return undefined;
		}
		// Through {@link signupChannelHeaderValue}, so the first call still records the landing: this can
		// run before the app-root effect that normally does, and whichever runs first captures the true one.
		derived = {channel: armedChannel(signupChannelHeaderValue(context), landedOnDownloadRoute(), recipientPageArm)};
	}
	return derived.channel;
}

/**
 * The measurement headers for one page load's answers — the whole rule about WHICH headers travel.
 *
 * Takes its answers rather than asking for them, so the rule is a pure function with an exhaustive
 * unit test rather than a branch reachable only through module memory and a real browser.
 *
 * An absent channel produces NO header, never a placeholder: a word for "none" would mint a series for
 * the ordinary signup.
 *
 * `visitStart` adds the visit marker (ui#2805) only beside a channel: the api counts a visit under the
 * channel it arrived by, so a marker without one would be a visit counted nowhere.
 */
export function measurementHeaders(
	channel: AttributionChannel | undefined, visitStart: boolean,
): Record<string, string> {
	const headers: Record<string, string> = {};
	if (channel !== undefined) {
		headers[SIGNUP_CHANNEL_HEADER] = channel;
		if (visitStart) {
			headers[VISIT_START_HEADER] = VISIT_START_VALUE;
		}
	}
	return headers;
}

/**
 * Whether this page load has already sent its visit marker (ui#2805). Module memory, so a full page load —
 * which re-evaluates this module — starts unmarked, and an in-app navigation does not.
 */
let visitMarkerSent = false;

/**
 * The measurement headers for the next outbound api request of this page load.
 *
 * The visit marker goes on the first request whose headers carry a CHANNEL, not simply the first request:
 * while the sign-in redirect verdict is out the channel may be withheld (see
 * {@link holdSignupChannelReferrer}), and outside a browser there is none at all. Spending the marker on
 * such a request would lose the visit, because the api counts it only under a channel. Synchronous between
 * reading and setting the flag, so two concurrent requests cannot both carry it.
 */
export function browserMeasurementHeaders(): Record<string, string> {
	const headers = measurementHeaders(browserSignupChannel(), !visitMarkerSent);
	if (headers[VISIT_START_HEADER] !== undefined) {
		visitMarkerSent = true;
	}
	return headers;
}

/**
 * Stop classifying this page load's referrer until the sign-in layer has ruled on it (ui#2516).
 *
 * Called from `AxiosUtils` as the transport is wired, which is the ONLY place guaranteed to run before
 * the first api request: the session bootstrap is a React effect behind a dynamic import, and on a
 * redirect return the account-establishing `GET /account` is issued from inside it while
 * `getRedirectResult` is still settling. Measured on the local-stack journey, where that request left
 * carrying the auth handler's `referral`.
 *
 * Only the REFERRER is withheld. A bucket the landing URL proves — a paid click, a recipient or
 * file-request route, a referral link, one of our own social campaign labels — is answered throughout,
 * so the establishing request of those arrivals is still counted correctly.
 */
export function holdSignupChannelReferrer(): void {
	holdReferrerClassification();
	derived = null;
}

/**
 * The sign-in layer's verdict on this page load (ui#1368, ui#2516): whether it is the return leg of a
 * provider sign-in redirect.
 *
 * Called from `firebaseSession`'s bootstrap once `completeRedirectSignIn` has settled, which is the one
 * moment this app honestly knows the answer. It DROPS the memo as well as recording the verdict, because
 * {@link browserSignupChannel} is first-call-wins for the page load and the requests made while the
 * verdict was out have already filled it with the withheld answer.
 *
 * Re-deriving is cheap and safe: the landing is unchanged module memory, so only the referrer's reading
 * changes.
 */
export function settleSignInRedirectVerdict(returnLeg: boolean): void {
	settleRedirectVerdict(returnLeg);
	derived = null;
}

/** The browser's own landing context, or `null` outside a browser (SSG pre-render, Node tests). */
export function browserLandingContext(): LandingContext | null {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return null;
	}
	return {
		pathname: window.location.pathname,
		referrer: document.referrer,
		search: window.location.search,
		host: window.location.hostname,
	};
}
